/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState } from '../../stores/types/types';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { FEED_TYPES } from '../../stores/reducers/shots';
import Socket from '../../connection/socket';
import MainVideoLive from './main-video-live';
import MainVideoEditing from './main-video-editing';
import * as SessionActions from '../../stores/actions/session';
import * as VideoActions from '../../stores/actions/video';
import useShots from '../../contexts/shots/provider';
import * as ModalActions from '../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import SpotlightManager from '../spotlight/spotlight-manager';
import { TitlesProvider } from '../../contexts/titles/titles-context';
import { ProgramViewType } from '../../stores/types/session';
import HoldSlide from './onsite-location/hold-slide';

interface OwnProps {
	socket: Socket | null;
}

export default function MainVideoContainer(props: OwnProps) {
	const currentUser = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const hasUpdatedState = useSelector(
		(state: GreenroomState) => state.SessionReducer.hasUpdatedState,
	);
	const session = useSelector((state: GreenroomState) => state.SessionReducer.session);
	const embed = useSelector((state: GreenroomState) => state.SessionReducer.embed);
	const programView = useSelector((state: GreenroomState) => state.SessionReducer.programView);
	const roomState = useSelector((state: GreenroomState) => state.MeetingReducer.roomState);
	const userIsMuted = useSelector((state: GreenroomState) => state.VideoReducer.audioMuted);
	const modalOpen = useSelector((state: GreenroomState) => state.ModalReducer.modalOpen);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const enablerShotId = useSelector((state: GreenroomState) => state.MeetingReducer.enablerShotId);

	const { socket } = props;
	const dispatch = useDispatch();
	const { currentShot } = useShots();

	/* embed view has to have a few seconds of black frames so
	that ffmpeg doesn't get overloaded when it first starts
	de-renders the <MainVideoLive> component */

	const [loadedMediaFeedsCount, setLoadedMediaFeedsCount] = useState<number>(0);

	const enablerShot = useMemo(() => {
		return shots.find((shot) => shot.shot_id === enablerShotId) ?? null;
	}, [enablerShotId, shots]);

	const enablerShotMediaFeedCount = useMemo(() => {
		if (!enablerShot?.shot_data?.shotFeeds) return 0;

		return enablerShot.shot_data.shotFeeds.filter((shotFeed) => (
			(shotFeed.feedType === FEED_TYPES.MEDIA || shotFeed.feedType === FEED_TYPES.MEDIA_AUDIO)
			&& !!shotFeed.media
		)).length;
	}, [enablerShot]);

	useEffect(() => {
		setLoadedMediaFeedsCount(0);
	}, [enablerShot?.shot_id]);

	useEffect(() => {
		document.body.removeAttribute('data-enabler-shot-ready');
		document.body.removeAttribute('data-enabler-shot-ready-timeout');
		document.body.removeAttribute('data-enabler-shot-ready-state');
		document.body.removeAttribute('data-enabler-shot-ready-debug');

		if (!enablerShot) return undefined;

		let cancelled = false;

		const wait = (ms: number): Promise<void> => new Promise((resolve) => {
			setTimeout(resolve, ms);
		});

		const isVisible = (element: Element) => {
			const rect = element.getBoundingClientRect();
			const style = window.getComputedStyle(element);

			return rect.width > 0
				&& rect.height > 0
				&& style.display !== 'none'
				&& style.visibility !== 'hidden'
				&& style.opacity !== '0';
		};

		const getVisibleImages = () => Array.from(document.querySelectorAll<HTMLImageElement>('img'))
			.filter((image) => isVisible(image) && !!(image.currentSrc || image.src));

		const getVisibleVideos = () => Array.from(document.querySelectorAll<HTMLVideoElement>('video'))
			.filter((video) => isVisible(video) && !!(video.currentSrc || video.src));

		const getPendingMediaFeeds = () => {
			if (!enablerShot?.shot_data?.shotFeeds) return [];

			return enablerShot.shot_data.shotFeeds
				.filter((shotFeed) => (
					(shotFeed.feedType === FEED_TYPES.MEDIA || shotFeed.feedType === FEED_TYPES.MEDIA_AUDIO)
					&& !!shotFeed.media
				))
				.slice(Math.max(loadedMediaFeedsCount, 0))
				.map((shotFeed) => ({
					feedType: shotFeed.feedType,
					media: shotFeed.media,
				}));
		};

		const setReadyDebug = (
			state: string,
			startedAt: number,
			visibleImages: HTMLImageElement[] = getVisibleImages(),
			visibleVideos: HTMLVideoElement[] = getVisibleVideos(),
		) => {
			document.body.setAttribute('data-enabler-shot-ready-state', state);
			document.body.setAttribute('data-enabler-shot-ready-debug', JSON.stringify({
				state,
				shotId: enablerShot.shot_id,
				elapsedMs: Date.now() - startedAt,
				expectedMediaFeeds: enablerShotMediaFeedCount,
				loadedMediaFeedsCount,
				pendingMediaFeeds: getPendingMediaFeeds(),
				images: visibleImages.map((image) => ({
					src: image.currentSrc || image.src,
					complete: image.complete,
					naturalWidth: image.naturalWidth,
					naturalHeight: image.naturalHeight,
					width: image.width,
					height: image.height,
					visible: isVisible(image),
				})),
				videos: visibleVideos.map((video) => ({
					src: video.currentSrc || video.src,
					readyState: video.readyState,
					networkState: video.networkState,
					videoWidth: video.videoWidth,
					videoHeight: video.videoHeight,
					currentTime: video.currentTime,
					duration: Number.isFinite(video.duration) ? video.duration : null,
					paused: video.paused,
					visible: isVisible(video),
					error: video.error ? {
						code: video.error.code,
						message: video.error.message,
					} : null,
				})),
			}));
		};

		const waitForVideoFrame = (video: HTMLVideoElement, startedAt: number): Promise<void> => new Promise((resolve) => {
			if (!isVisible(video) || video.readyState < 2) {
				resolve();
				return;
			}

			if (video.paused) {
				resolve();
				return;
			}

			if (video.requestVideoFrameCallback) {
				setReadyDebug('waiting-video-frame', startedAt);
				video.requestVideoFrameCallback(() => resolve());
				return;
			}

			requestAnimationFrame(() => resolve());
		});

		const getRunningAnimationsCount = () => document.getAnimations()
			.filter((animation) => animation.playState === 'running').length;

		const waitForAnimationsToSettle = async (startedAt: number): Promise<void> => {
			const animationWaitStartedAt = Date.now();

			while (!cancelled && getRunningAnimationsCount() > 0 && Date.now() - animationWaitStartedAt < 1500) {
				setReadyDebug('waiting-animations', startedAt);
				await wait(50);
			}
		};

		const waitForPaintSettle = async (startedAt: number): Promise<void> => {
			await waitForAnimationsToSettle(startedAt);

			return new Promise((resolve) => {
				setReadyDebug('paint-settle', startedAt);
				requestAnimationFrame(() => {
					requestAnimationFrame(() => resolve());
				});
			});
		};

		const waitForEnablerShotReady = async () => {
			const startedAt = Date.now();
			setReadyDebug('initializing', startedAt);

			while (!cancelled && Date.now() - startedAt < 10000) {
				const loadedExpectedMediaFeeds = loadedMediaFeedsCount >= enablerShotMediaFeedCount;
				const visibleImages = getVisibleImages();
				const visibleVideos = getVisibleVideos();

				const imagesReady = visibleImages.every((image) => image.complete && image.naturalWidth > 0);
				const videosReady = visibleVideos.every((video) => (
					video.readyState >= 2
					&& video.videoWidth > 0
					&& video.videoHeight > 0
				));

				if (!loadedExpectedMediaFeeds) {
					setReadyDebug('waiting-media-feeds', startedAt, visibleImages, visibleVideos);
				} else if (!imagesReady) {
					setReadyDebug('waiting-images', startedAt, visibleImages, visibleVideos);
				} else if (!videosReady) {
					setReadyDebug('waiting-videos', startedAt, visibleImages, visibleVideos);
				} else if (getRunningAnimationsCount() > 0) {
					setReadyDebug('waiting-animations', startedAt, visibleImages, visibleVideos);
				}

				if (loadedExpectedMediaFeeds && imagesReady && videosReady) {
					await Promise.all(visibleVideos.map((video) => waitForVideoFrame(video, startedAt)));
					await waitForPaintSettle(startedAt);

					if (!cancelled) {
						setReadyDebug('ready', startedAt, getVisibleImages(), getVisibleVideos());
						document.body.setAttribute('data-enabler-shot-ready', 'true');
					}
					return;
				}

				await wait(50);
			}

			if (!cancelled) {
				setReadyDebug('timeout', startedAt);
				document.body.setAttribute('data-enabler-shot-ready-timeout', 'true');
			}
		};

		void waitForEnablerShotReady();

		return () => {
			cancelled = true;
			document.body.removeAttribute('data-enabler-shot-ready');
			document.body.removeAttribute('data-enabler-shot-ready-timeout');
			document.body.removeAttribute('data-enabler-shot-ready-state');
			document.body.removeAttribute('data-enabler-shot-ready-debug');
		};
	}, [enablerShot, enablerShotMediaFeedCount, loadedMediaFeedsCount]);

	useEffect(() => {
		// We display a "view program feed" modal when a user opens
		// the program feed requiring the user to click to close
		// to avoid Chrome or any browsers autoplay policy from stopping the feed from playing (https://developer.chrome.com/blog/autoplay/)
		if (programView === ProgramViewType.ProgramFeed) {
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.PROGRAM_FEED,
			}));
		}
		if (programView === ProgramViewType.OnsiteFeed) {
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.ONSITE_FEED,
			}));
		}
	}, []);

	useEffect(() => {
		if (!hasUpdatedState && session) {
			socket?.sendMessage('request-state', session);
			dispatch(SessionActions.setHasUpdatedState(true));
		}
	}, [hasUpdatedState, currentUser]);

	useEffect(() => {
		if (roomState && !userIsMuted) {
			dispatch(VideoActions.resetAudioMutedPrefs());
		}
	}, [dispatch, roomState, userIsMuted]);

	const iterateLoadedMediaCount = () => {
		setLoadedMediaFeedsCount((count) => count + 1);
	};

	// onsite and program feed must dismiss a modal before displaying MainVideoLive
	// embed view must have a server state of started before fully displaying
	//		when not, display an overlay to better allow bullman to startup
	return (
		<TitlesProvider
			titlesOn={enablerShot ? enablerShot.shot_data.videoTitlesOn : currentShot.shot_data.videoTitlesOn}
			shotId={enablerShot ? enablerShot.shot_id : currentShot.shot_id}
			isCanvas={true}
		>
			{embed && !enablerShot && <>
				{
					// if program view is program feed or onsite feed
					// do not render until the modal is closed
					(
						programView === ProgramViewType.None
						|| programView === ProgramViewType.OnsiteLocation
						|| !modalOpen
					)
					&& roomState === ROOM_STATES.SHOWTIME
						? (
							<>
								<div id='hidden-spotlight' className={classNames('spotlights', 'spotlight-container')} style={{ position: 'absolute', height: '1080px', width: '1920px' }}>
									<SpotlightManager socket={socket} iterateLoadedMediaCount={() => {}}/>
								</div>
								<MainVideoLive socket={props.socket} />
							</>
						) : (
							<HoldSlide
								showHoldSlide={true}
								showVideoWrapper={programView !== ProgramViewType.OnsiteLocation}
							/>
						)
				}
			</>}

			{enablerShot && (
				<MainVideoEditing
					socket={props.socket}
					shotBeingEdited={enablerShot}
					isForShotEditorModal={false}
					iterateLoadedMediaCount={iterateLoadedMediaCount}
				/>
			)}

			{!embed && <>
				{roomState === ROOM_STATES.SHOWTIME
					&& <>
						<div
							id='hidden-spotlight'
							className={classNames('spotlights', 'spotlight-container')}
							style={{ position: 'absolute', height: '1080px', width: '1920px' }}
						>
							<SpotlightManager socket={socket} iterateLoadedMediaCount={() => {}}/>
						</div>
						<MainVideoLive
							socket={props.socket}
						/>
					</>
				}

				{roomState === ROOM_STATES.EDITING
					&& <MainVideoEditing
						socket={props.socket}
						shotBeingEdited={currentShot}
						isForShotEditorModal={false}
						iterateLoadedMediaCount={iterateLoadedMediaCount}
					/>
				}
			</>}

			{!embed && <div id="media-modal-portal" />}

		</TitlesProvider>
	);
}
